import { Button } from './button';
import { useI18n } from '@/hooks/use-i18n';

interface IProps {
  page: number;
  count: number;
  onChange: (val: number) => void;
}

export default function Pagination(props: IProps) {
  const { page, count, onChange } = props;
  const { t } = useI18n('common');

  const getButton = (current: number) => (
    <Button
      key={current}
      variant={`${page === current ? 'default' : 'ghost'}`}
      size="sm"
      className={`${page === current ? 'bg-[#7171ee] hover:bg-[#5959c7] text-white' : 'text-gray-600'} h-8 w-8 p-0 px-4 rounded-[4px]`}
      onClick={() => onChange(current)}
    >
      {current}
    </Button>
  );

  // 生成分页按钮
  const renderPaginationButtons = () => {
    // 如果总页数小于等于7，则全部显示
    if (count <= 7) {
      return Array(count)
        .fill(0)
        .map((_, index) => getButton(index + 1));
    }

    // 总页数大于等于7的情况
    const buttons = [];

    // 第一个位置始终是第一页
    buttons.push(getButton(1));

    // 第二个位置：根据当前页码决定是显示第二页还是省略号
    if (page <= 4) {
      // 当前页码在前四页，显示第二页
      buttons.push(getButton(2));
    } else {
      // 当前页码超过第四页，显示省略号
      buttons.push(
        <span
          key="ellipsis-start"
          className="h-8 w-8 flex items-end justify-center pb-1"
        >
          ...
        </span>,
      );
    }

    // 中间三个位置：当前页及其相邻的两页
    if (page <= 3) {
      // 当前页在前三页，显示3、4、5
      buttons.push(getButton(3));
      buttons.push(getButton(4));
      buttons.push(getButton(5));
    } else if (page >= count - 2) {
      // 当前页在后三页，显示倒数第四、第三、第二页
      buttons.push(getButton(count - 4));
      buttons.push(getButton(count - 3));
      buttons.push(getButton(count - 2));
    } else {
      // 其他情况，显示当前页及其前后页
      buttons.push(getButton(page - 1));
      buttons.push(getButton(page));
      buttons.push(getButton(page + 1));
    }

    // 倒数第二个位置：根据当前页码决定是显示倒数第二页还是省略号
    if (page >= count - 3) {
      // 当前页码在后四页，显示倒数第二页
      buttons.push(getButton(count - 1));
    } else {
      // 当前页码不在后四页，显示省略号
      buttons.push(
        <span
          key="ellipsis-end"
          className="h-8 w-8 flex items-end justify-center pb-1"
        >
          ...
        </span>,
      );
    }

    // 最后一个位置始终是最后一页
    buttons.push(getButton(count));

    return buttons;
  };

  return (
    <div className="flex items-center justify-center gap-2">
      {count > 1 && (
        <Button
          variant="outline"
          size="sm"
          className="flex items-center px-[16px] py-1 leading-[30px] rounded-[4px] border-[#E5E7EB] h-auto"
          disabled={page === 1}
          onClick={() => page > 1 && onChange(page - 1)}
        >
          <span className="hidden sm:inline">
            {t('pagination.previousPage')}
          </span>
        </Button>
      )}

      <div className="hidden sm:flex items-center gap-2">
        {renderPaginationButtons()}
      </div>

      <div className="sm:hidden flex items-center">
        <span className="text-sm text-gray-500">
          {page} / {count}
        </span>
      </div>

      {count > 1 && (
        <Button
          variant="outline"
          size="sm"
          className="flex items-center px-[16px] py-1 leading-[30px] rounded-[4px] border-[#E5E7EB] h-auto"
          disabled={page === count}
          onClick={() => page < count && onChange(page + 1)}
        >
          <span className="hidden sm:inline">{t('pagination.nextPage')}</span>
        </Button>
      )}
    </div>
  );
}
